import { Sparkles, Users, MessageSquare, Palette } from 'lucide-react';

// field types: 'text' | 'textarea' | 'array' | 'scale' | 'color_pair'
// scale: 0–10 visual bar + range input
// color_pair: { value: '#hex', role: string } — shown as swatch + role label
// array: string[] — shown as tag chips

export const PILLAR_CONFIG = {
  core: {
    label: 'Core Identity',
    Icon: Sparkles,
    accent: '#FF7E67',
    groups: [
      {
        title: 'What We Do',
        fields: [
          { key: 'mission', label: 'Mission', type: 'textarea', hint: 'Your brand\'s core purpose in one line' },
          { key: 'value_propositions', label: 'Value Propositions', type: 'array', hint: 'What makes you uniquely valuable?' },
          { key: 'product_categories', label: 'Product Categories', type: 'array', hint: 'What types of products or services?' },
        ],
      },
      {
        title: 'Vocabulary',
        fields: [
          { key: 'keywords_use', label: 'Keywords to Use', type: 'array', hint: 'Words that reinforce your brand' },
          { key: 'keywords_avoid', label: 'Keywords to Avoid', type: 'array', hint: 'Words that dilute your brand' },
          { key: 'content_themes', label: 'Content Themes', type: 'array', hint: 'Recurring topics that fit your brand' },
        ],
      },
      {
        title: 'Copy Examples',
        fields: [
          { key: 'good_copy_examples', label: 'Good Copy Examples', type: 'array', hint: 'Captions or headlines that worked well' },
          { key: 'bad_copy_examples', label: 'Copy to Avoid', type: 'array', hint: 'Examples of tone you dislike' },
        ],
      },
    ],
  },

  market: {
    label: 'Your Market',
    Icon: Users,
    accent: '#f093fb',
    groups: [
      {
        title: 'Audience',
        fields: [
          { key: 'target_age_range', label: 'Age Range', type: 'text', hint: 'e.g. 25–40' },
          { key: 'target_gender', label: 'Gender Focus', type: 'text', hint: 'e.g. All, Primarily female' },
          { key: 'target_location', label: 'Location', type: 'text', hint: 'Cities, countries, or regions' },
        ],
      },
      {
        title: 'Psychology',
        fields: [
          { key: 'pain_points', label: 'Pain Points', type: 'array', hint: 'What problems does your audience face?' },
          { key: 'buying_triggers', label: 'Buying Triggers', type: 'array', hint: 'What motivates them to buy?' },
          { key: 'customer_mindset', label: 'Customer Mindset', type: 'textarea', hint: 'How do they think and feel when shopping?' },
        ],
      },
      {
        title: 'Positioning',
        fields: [
          { key: 'differentiator', label: 'Differentiator', type: 'textarea', hint: 'What sets you apart from competitors?' },
        ],
      },
    ],
  },

  voice: {
    label: 'Brand Voice',
    Icon: MessageSquare,
    accent: '#fa8c16',
    groups: [
      {
        title: 'Tone',
        fields: [
          { key: 'tone_primary', label: 'Primary Tone', type: 'text', hint: 'e.g. Bold, Playful, Professional' },
          { key: 'tone_descriptors', label: 'Tone Descriptors', type: 'array', hint: 'Adjectives that describe your voice' },
          { key: 'personality_if_human', label: 'Brand Personality', type: 'textarea', hint: 'If your brand were a person, who would they be?' },
        ],
      },
      {
        title: 'Style',
        fields: [
          { key: 'sentence_style', label: 'Sentence Style', type: 'text', hint: 'e.g. Short punchy sentences' },
          { key: 'perspective', label: 'Perspective', type: 'text', hint: 'e.g. First-person plural (We)' },
        ],
      },
      {
        title: 'Personality Scales',
        fields: [
          { key: 'formality_level', label: 'Formality', type: 'scale', hint: 'Casual ← 0 to 10 → Formal' },
          { key: 'humor_level', label: 'Humor', type: 'scale', hint: 'Serious ← 0 to 10 → Playful' },
          { key: 'energy_level', label: 'Energy', type: 'scale', hint: 'Calm ← 0 to 10 → Energetic' },
        ],
      },
      {
        title: 'Communication Rules',
        fields: [
          { key: 'sample_phrases', label: 'Sample Phrases', type: 'array', hint: 'Phrases that sound like your brand' },
          { key: 'communication_dos', label: 'Do\'s', type: 'array', hint: 'What to always include' },
          { key: 'communication_donts', label: 'Don\'ts', type: 'array', hint: 'What to never say' },
        ],
      },
    ],
  },

  visual: {
    label: 'Visual Style',
    Icon: Palette,
    accent: '#52c41a',
    groups: [
      {
        title: 'Color Palette',
        fields: [
          { key: 'color_primary', label: 'Primary Color', type: 'color_pair', roleKey: 'color_primary_role', hint: 'Main brand color' },
          { key: 'color_secondary', label: 'Secondary Color', type: 'color_pair', roleKey: 'color_secondary_role', hint: 'Supporting color' },
          { key: 'color_accent', label: 'Accent Color', type: 'color_pair', roleKey: 'color_accent_role', hint: 'Used for highlights and CTAs' },
          { key: 'color_background', label: 'Background Color', type: 'color', hint: 'Primary background' },
          { key: 'color_text', label: 'Text Color', type: 'color', hint: 'Default text color' },
        ],
      },
      {
        title: 'Mood & Aesthetic',
        fields: [
          { key: 'aesthetic_category', label: 'Aesthetic Category', type: 'text', hint: 'e.g. Minimalist, Luxury, Streetwear' },
          { key: 'mood', label: 'Mood', type: 'text', hint: 'e.g. Warm, Energetic, Serene' },
          { key: 'visual_density', label: 'Visual Density', type: 'scale', hint: 'Minimal ← 0 to 10 → Rich' },
        ],
      },
      {
        title: 'Photography',
        fields: [
          { key: 'photography_style', label: 'Photography Style', type: 'text', hint: 'e.g. Studio, Lifestyle, Documentary' },
          { key: 'photography_style_detail', label: 'Photography Detail', type: 'textarea', hint: 'Describe how photos should feel' },
          { key: 'composition_preferences', label: 'Composition Preferences', type: 'array', hint: 'e.g. Rule of thirds, Close-ups' },
        ],
      },
      {
        title: 'Typography',
        fields: [
          { key: 'typography_personality', label: 'Typography Personality', type: 'text', hint: 'e.g. Bold geometric, Classic serif' },
          { key: 'heading_fonts', label: 'Heading Fonts', type: 'array', hint: 'Font names for headlines' },
          { key: 'body_fonts', label: 'Body Fonts', type: 'array', hint: 'Font names for body text' },
        ],
      },
      {
        title: 'Rules',
        fields: [
          { key: 'visual_donts', label: 'Visual Don\'ts', type: 'array', hint: 'Visual elements to avoid' },
          { key: 'platform_guidelines', label: 'Platform Guidelines', type: 'array', hint: 'Platform-specific visual notes' },
        ],
      },
      {
        title: 'Brand Guidelines',
        fields: [
          { key: 'brand_design_description', label: 'Design Description', type: 'textarea', hint: 'Describe the overall visual design style of your brand', source: 'brand' },
          { key: 'extra_guidelines', label: 'Extra Guidelines', type: 'textarea', hint: 'Additional instructions for the AI when creating content', source: 'brand' },
        ],
      },
    ],
  },
};
